import { Progress, Space, Tag, Typography } from "antd";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import React from "react";
import type { SchemaRunStatus } from "@/hooks/useSchemaManagement";

const { Text } = Typography;

interface SchemaRunProgressProps {
	run: SchemaRunStatus;
}

export function SchemaRunProgress({ run }: SchemaRunProgressProps) {
	const getStatusIcon = () => {
		switch (run.status) {
			case "completed":
				return <CheckCircle size={16} className="text-green-500 dark:text-green-400" />;
			case "failed":
				return <XCircle size={16} className="text-red-500 dark:text-red-400" />;
			case "running":
				return <Loader2 size={16} className="text-blue-500 dark:text-blue-400 animate-spin" />;
			default:
				return null;
		}
	};

	const getStatusColor = () => {
		switch (run.status) {
			case "completed":
				return "success";
			case "failed":
				return "error";
			case "running":
				return "processing";
			default:
				return "default";
		}
	};

	const getStatusText = () => {
		switch (run.status) {
			case "completed":
				return "Completed";
			case "failed":
				return "Failed";
			case "running":
				return "Running";
			default:
				return "Unknown";
		}
	};

	return (
		<Space direction="vertical" size={8} style={{ width: "100%" }}>
			{/* Status and Progress Text */}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Space>
					{getStatusIcon()}
					<Tag color={getStatusColor()}>{getStatusText()}</Tag>
					<Text type="secondary" style={{ fontSize: 12 }}>
						{run.processed_items} / {run.total_items} items
					</Text>
				</Space>
				<Text strong>{run.progress_percent}%</Text>
			</div>

			{/* Progress Bar */}
			<Progress
				percent={run.progress_percent}
				status={
					run.status === "failed"
						? "exception"
						: run.status === "completed"
							? "success"
							: "active"
				}
				strokeColor={
					run.status === "failed"
						? "#ff4d4f"
						: run.status === "completed"
							? "#52c41a"
							: "#1677ff"
				}
				showInfo={false}
			/>

			{/* Error Message */}
			{run.status === "failed" && run.error_message && (
				<Text type="danger" style={{ fontSize: 12 }}>
					Error: {run.error_message}
				</Text>
			)}
		</Space>
	);
}
